const PASSIVE_MEDIA = /^(?:image\/(?:png|jpeg|gif|webp|bmp|avif|x-icon|vnd\.microsoft\.icon)|audio\/(?:mpeg|mp3|mp4|aac|ogg|wav|webm|flac|opus)|video\/(?:mp4|webm|ogg|quicktime|x-m4v))$/;
const MIME_ALIASES: Record<string, string> = {"application/x-pdf": "application/pdf", "audio/x-m4a": "audio/mp4", "audio/m4a": "audio/mp4", "audio/x-wav": "audio/wav", "audio/wave": "audio/wav", "audio/x-flac": "audio/flac", "application/ogg": "audio/ogg", "image/jpg": "image/jpeg", "image/x-png": "image/png"};
const MEDIA_EXTENSIONS: Record<string, string> = {png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp", bmp: "image/bmp", avif: "image/avif", mp3: "audio/mpeg", m4a: "audio/mp4", wav: "audio/wav", aac: "audio/aac", flac: "audio/flac", oga: "audio/ogg", ogg: "audio/ogg", opus: "audio/opus", mp4: "video/mp4", webm: "video/webm", mov: "video/quicktime", m4v: "video/x-m4v"};
const canonicalMime = (type: string) => {
    const normalized = type.split(";")[0].trim().toLowerCase();
    const alias = MIME_ALIASES[normalized];
    return typeof alias === "string" ? alias : normalized;
};

export function canViewFileBlob(blob: Blob): boolean {
    return blob.type === "application/pdf" || PASSIVE_MEDIA.test(blob.type);
}

/** Never let attachment metadata promote active content to an app-origin document. */
export async function prepareFileBlob(blob: Blob, contentType = "", fileName = ""): Promise<Blob> {
    const type = canonicalMime(blob.type);
    if (PASSIVE_MEDIA.test(type)) return blob.slice(0, blob.size, type);
    const generic = !type || type === "application/octet-stream" || type === "binary/octet-stream";
    if (type === "application/pdf" || generic) {
        const header = await blob.slice(0, 1024).text();
        if (/%PDF-\d\.\d/.test(header)) return blob.slice(0, blob.size, "application/pdf");
    }
    if (generic) {
        // Hints may recover passive media only; they never promote a PDF or active document.
        const hint = canonicalMime(contentType);
        const extension = fileName.split(/[?#]/)[0].split(".").pop()?.toLowerCase() || "";
        const media = PASSIVE_MEDIA.test(hint) ? hint : MEDIA_EXTENSIONS[extension];
        if (typeof media === "string") return blob.slice(0, blob.size, media);
    }
    return blob.slice(0, blob.size, "application/octet-stream");
}

/** HTTP(S) stays external; local active/unknown attachments are download-only. */
export async function openFileUrl(url: string, fileName = "attachment"): Promise<void> {
    if (!url.startsWith("blob:")) {
        if (!/^https?:\/\//i.test(url)) throw new Error("Unsupported file URL");
        window.open(url, "_blank", "noopener,noreferrer");
        return;
    }
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed to load file");
    const blob = await prepareFileBlob(await response.blob());
    const preparedUrl = URL.createObjectURL(blob);
    if (canViewFileBlob(blob)) {
        window.open(preparedUrl, "_blank", "noopener,noreferrer");
    } else {
        const link = document.createElement("a");
        link.href = preparedUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
    }
    // A new viewer/download must get time to consume the Blob.
    window.setTimeout(() => URL.revokeObjectURL(preparedUrl), 60_000);
}
